import React, { useCallback, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  AddRounded,
  ArrowDropDown as ArrowDropDownIcon,
  FormatListBulletedRounded,
  InfoOutlined,
  SearchRounded,
  TuneRounded,
} from "@mui/icons-material";
import { useReducedMotion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { useWatchlistEarnings } from "../../../../hooks/useWatchlistEarnings";
import { MOBILE_TOPBAR_HEIGHT } from "../../../../contexts/SidebarContext";
import { useTopBarActions } from "../../../../contexts/TopBarSlotContext";
import { hapticSelection } from "../../../../lib/haptics";
import { isNative } from "../../../../lib/platform";
import AdaptiveDialog from "../../../common/AdaptiveDialog";
import ErrorDisplay from "../../../common/ErrorDisplay/ErrorDisplay";
import SearchBar from "../../../common/SearchBar/SearchBar";
import { focusRingSx, touchTargetSx } from "../../overviewPanel";
import { tonalTokens } from "../../../polish/tokens";
import { MAX_WATCHLISTS_PER_USER } from "../../Watchlists/watchlistLimits";
import WatchlistSwitcher from "../../Watchlists/WatchlistSwitcher";
import WatchlistNameModal from "../../Watchlists/WatchlistNameModal";
import ManageWatchlistModal from "../../Watchlists/ManageWatchlistModal";
import { useEarningsFeed } from "../hooks/useEarningsFeed";
import { useEarningsScope } from "../hooks/useEarningsScope";
import { useMinute } from "../hooks/useMinute";
import { EarningsLocked } from "../EarningsLocked";
import { EarningsFeedCapabilitiesSheet } from "../EarningsFeedCapabilitiesSheet";
import { earningsCompanyPath, earningsReportPath } from "../earningsNavigation";
import { EarningsFeed } from "../feed/EarningsFeed";
import { FeedSkeleton, ResultsCardSkeleton } from "../feed/FeedSkeletons";
import { SECTION_TITLE_GAP, sectionTitleSx } from "../feed/headings";
import { NewItemsPill } from "../feed/NewItemsPill";
import { ThisWeek } from "../feed/ThisWeek";
import { rememberFeedAnchor } from "../feed/feedAnchor";
import { flattenPages } from "../feed/feedModel";
import { reportedKeys, thisWeekDays } from "../feed/thisWeekModel";
import type { EarningsFeedItem } from "../feed/types";

export const EMPTY_FEED_LINE =
  "Results appear shortly after your companies report";

const NO_ITEMS: EarningsFeedItem[] = [];

/**
 * The hub's one centred column: a report's results card and notes card side
 * by side; wider only spreads the eye across empty space.
 */
export const FEED_COLUMN_WIDTH = 1240;

// Matches the 34px switcher height so the controls sit flush with it.
const iconButtonSx = (isDark: boolean, idleColor: string) => ({
  width: 34,
  height: 34,
  color: idleColor,
  transition: "color 0.18s ease, background-color 0.18s ease",
  "&:hover": {
    color: "#22c55e",
    backgroundColor: isDark ? "rgba(34,197,94,0.1)" : "rgba(34,197,94,0.05)",
  },
  "&:focus-visible": { outline: "2px solid #22c55e", outlineOffset: -2 },
});

interface HubTabProps {
  /** The Earnings Hub view is the one showing; only then does it own the top bar. */
  active?: boolean;
  /**
   * The page's header row, beside the view switch: on wide screens the list
   * controls and search render there. Without it they head this view.
   */
  headerSlot?: HTMLElement | null;
}

/**
 * The Earnings Hub view: one centred column on every screen. Your companies
 * reporting this week, then one card per report they have made, newest
 * report first: the results, how many analyst notes followed and the newest
 * few. A card opens its report page, where every note is. On wide screens
 * the list controls join the view switch's row.
 */
const HubTab: React.FC<HubTabProps> = ({
  active = true,
  headerSlot = null,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tokens = tonalTokens(isDark);
  const navigate = useNavigate();
  const location = useLocation();
  const native = isNative();
  // Below lg the web shell has the mobile top bar, as native always does.
  const topBar = useMediaQuery(theme.breakpoints.down("lg")) || native;
  const reduced = useReducedMotion() === true;
  const now = useMinute();

  const [switcherAnchor, setSwitcherAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isManageOpen, setIsManageOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isExplainOpen, setIsExplainOpen] = useState(false);
  const [column, setColumn] = useState<HTMLElement | null>(null);

  const {
    watchlists,
    selectedWatchlist,
    selectedWatchlistId,
    selectWatchlist,
    all,
    selectAll,
    selectList,
    scopeId,
    isLoading: watchlistsLoading,
  } = useEarningsScope();

  const hasNoWatchlists = !watchlistsLoading && watchlists.length === 0;
  const hasScope = !watchlistsLoading && (all || selectedWatchlistId !== null);
  const feed = useEarningsFeed({
    watchlistId: scopeId,
    enabled: hasScope && watchlists.length > 0,
  });
  const upcoming = useWatchlistEarnings(
    hasNoWatchlists ? null : selectedWatchlistId,
    { allLists: all && !hasNoWatchlists },
  );

  const pages = feed.data?.pages;
  const items = useMemo(
    () => (pages === undefined ? NO_ITEMS : flattenPages(pages)),
    [pages],
  );
  const upcomingEntries = upcoming.data?.data;
  const days = useMemo(
    () => thisWeekDays(upcomingEntries ?? [], reportedKeys(items), now),
    [upcomingEntries, items, now],
  );

  const atWatchlistLimit = watchlists.length >= MAX_WATCHLISTS_PER_USER;
  const limitReason = atWatchlistLimit
    ? `Limit of ${MAX_WATCHLISTS_PER_USER} watchlists reached`
    : undefined;
  const switcherLabel = all
    ? "All watchlists"
    : (selectedWatchlist?.name ?? "Your Watchlist");

  const openCompany = useCallback(
    (ticker: string) => {
      hapticSelection();
      navigate(earningsCompanyPath(ticker));
    },
    [navigate],
  );
  // Back returns to this entry: remember where the opened card sat, so the
  // feed puts it back in the first frame.
  const entry = location.key;
  const openReport = useCallback(
    (eventId: string, key: string, card: HTMLElement) => {
      hapticSelection();
      rememberFeedAnchor(entry, { key, top: card.getBoundingClientRect().top });
      navigate(earningsReportPath(eventId));
    },
    [entry, navigate],
  );
  const openSwitcher = useCallback((anchor: HTMLElement) => {
    hapticSelection();
    setSwitcherAnchor(anchor);
  }, []);

  const topBarActions = useMemo(
    () => (
      <>
        <IconButton
          size="small"
          aria-label={`Watchlist: ${switcherLabel}`}
          aria-haspopup="menu"
          onClick={(event) => openSwitcher(event.currentTarget)}
          sx={{ color: "text.secondary", ...touchTargetSx, ...focusRingSx }}
        >
          <FormatListBulletedRounded fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          aria-label="Find a company"
          onClick={() => {
            hapticSelection();
            setIsSearchOpen(true);
          }}
          sx={{ color: "text.secondary", ...touchTargetSx, ...focusRingSx }}
        >
          <SearchRounded fontSize="small" />
        </IconButton>
      </>
    ),
    [switcherLabel, openSwitcher],
  );
  useTopBarActions(
    topBarActions,
    active && topBar && !watchlistsLoading && !hasNoWatchlists,
  );

  // Loaded cards stay on screen through any refetch or remount; only a feed
  // with no page yet shows its shapes.
  const firstLoad =
    watchlistsLoading || (hasScope && feed.isPending && !feed.isError);
  const firstLoadFailed = feed.isError && feed.data === undefined;
  const firstPage = pages?.[0];
  const locked = firstPage?.locked === true;
  // Native users are all members; a locked answer there shows no feed and no upsell.
  const feedHidden = locked && native;

  const sheets = (
    <>
      <WatchlistSwitcher
        open={switcherAnchor !== null}
        anchorEl={switcherAnchor}
        onClose={() => setSwitcherAnchor(null)}
        watchlists={watchlists}
        selectedWatchlistId={selectedWatchlistId}
        onSelect={selectList}
        onCreate={() => setIsCreateOpen(true)}
        onManage={selectedWatchlist ? () => setIsManageOpen(true) : undefined}
        createDisabledReason={limitReason}
        manageLabel={selectedWatchlist?.name}
        actionsInMenu={topBar}
        allOption={{ selected: all, onSelect: selectAll }}
      />
      <WatchlistNameModal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreated={(watchlist) => selectList(watchlist.id)}
      />
      <ManageWatchlistModal
        open={isManageOpen}
        onClose={() => setIsManageOpen(false)}
        watchlistId={selectedWatchlistId}
        // Fall back to the derived default rather than pointing at a dead id.
        onDeleted={() => selectWatchlist(null)}
      />
      <EarningsFeedCapabilitiesSheet
        open={isExplainOpen}
        onClose={() => setIsExplainOpen(false)}
      />
      {topBar && (
        <AdaptiveDialog
          open={isSearchOpen}
          onClose={() => setIsSearchOpen(false)}
          title="Find a company"
        >
          <SearchBar
            placeholder="Search any company..."
            width="100%"
            variant="chatgpt"
            mode="earnings"
          />
        </AdaptiveDialog>
      )}
    </>
  );

  // The list switcher, new list, manage and search: beside the view switch
  // on wide screens, heading this view otherwise.
  const controls = (
    <Box
      data-testid="hub-controls"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 1.5,
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          minWidth: 0,
        }}
      >
        <Box
          component="button"
          onClick={(event: React.MouseEvent<HTMLElement>) =>
            openSwitcher(event.currentTarget)
          }
          aria-haspopup="menu"
          aria-expanded={switcherAnchor !== null}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            minHeight: 34,
            px: 1.5,
            maxWidth: "100%",
            minWidth: 0,
            border: "none",
            borderRadius: "999px",
            backgroundColor: tokens.surface,
            color: tokens.text,
            cursor: "pointer",
            font: "inherit",
            fontSize: "0.8125rem",
            fontWeight: 600,
            WebkitTapHighlightColor: "transparent",
            transition: "background-color 0.15s ease, transform 0.1s ease",
            "&:hover": { backgroundColor: tokens.raised },
            "&:active": { transform: "scale(0.98)" },
            "&:focus-visible": {
              outline: `2px solid ${tokens.gain}`,
              outlineOffset: 2,
            },
            "@media (prefers-reduced-motion: reduce)": {
              transition: "none",
              "&:active": { transform: "none" },
            },
          }}
        >
          <Box
            component="span"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
            }}
          >
            {switcherLabel}
          </Box>
          {!all && selectedWatchlist && (
            <Box component="span" sx={{ color: tokens.muted, flex: "none" }}>
              · {selectedWatchlist.holdings_count}
            </Box>
          )}
          <ArrowDropDownIcon sx={{ fontSize: 18, flex: "none" }} />
        </Box>

        <Tooltip title={limitReason ?? "New watchlist"}>
          {/* Disabled buttons swallow pointer events, so the span carries the hover */}
          <span>
            <IconButton
              size="small"
              onClick={() => setIsCreateOpen(true)}
              disabled={atWatchlistLimit}
              aria-label="New watchlist"
              sx={iconButtonSx(isDark, tokens.muted)}
            >
              <AddRounded sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>

        {selectedWatchlist && (
          <Tooltip title={`Manage ${selectedWatchlist.name}`}>
            <IconButton
              size="small"
              onClick={() => setIsManageOpen(true)}
              aria-label={`Manage ${selectedWatchlist.name}`}
              sx={iconButtonSx(isDark, tokens.muted)}
            >
              <TuneRounded sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ flexShrink: 0 }}>
        <SearchBar
          placeholder="Search any company..."
          width={300}
          variant="compact"
          mode="earnings"
          height={34}
        />
      </Box>
    </Box>
  );

  // No watchlists at all: the signup hook failed, or every list was deleted.
  if (hasNoWatchlists)
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box
          data-testid="earnings-no-watchlist"
          sx={{
            maxWidth: 560,
            mx: "auto",
            pt: { xs: 6, md: 10 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
            textAlign: "center",
          }}
        >
          <Typography
            component="h2"
            sx={{
              fontSize: { xs: "1.375rem", md: "1.625rem" },
              fontWeight: 600,
              letterSpacing: "-0.01em",
              color: tokens.text,
            }}
          >
            Follow your companies' earnings
          </Typography>
          <Button
            variant="contained"
            disableElevation
            onClick={() => {
              hapticSelection();
              setIsCreateOpen(true);
            }}
            sx={{
              minHeight: 44,
              px: 3,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              backgroundColor: "#22c55e",
              color: "#ffffff",
              "&:hover": { backgroundColor: "#16a34a" },
            }}
          >
            Create a watchlist
          </Button>
          <Box sx={{ width: "100%" }}>
            <SearchBar
              placeholder="Search any company..."
              width="100%"
              variant="chatgpt"
              mode="earnings"
            />
          </Box>
        </Box>
        <WatchlistNameModal
          open={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          onCreated={(watchlist) => selectList(watchlist.id)}
        />
      </Box>
    );

  const ready = feed.data !== undefined;

  return (
    <Box data-testid="earnings-watchlist-view" sx={{ p: { xs: 2, md: 3 } }}>
      {!topBar && headerSlot === null && <Box sx={{ mb: 2.5 }}>{controls}</Box>}
      {!topBar &&
        active &&
        headerSlot !== null &&
        createPortal(controls, headerSlot)}

      <Box
        ref={setColumn}
        data-testid="hub-column"
        sx={{ maxWidth: FEED_COLUMN_WIDTH, mx: "auto" }}
      >
        <ThisWeek
          days={days}
          loading={
            watchlistsLoading ||
            (upcoming.data === undefined && !upcoming.isError)
          }
          failed={upcoming.data === undefined && upcoming.isError}
          onRetry={() => void upcoming.refetch()}
          tokens={tokens}
          onOpenCompany={openCompany}
        />

        {!feedHidden && (
          <Box
            component="section"
            aria-labelledby="reported-heading"
            sx={{ mt: { xs: 5, md: 6 } }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                mb: SECTION_TITLE_GAP,
              }}
            >
              <Typography
                id="reported-heading"
                component="h2"
                sx={sectionTitleSx(tokens)}
              >
                Reported
              </Typography>
              <IconButton
                size="small"
                aria-label="What's in this feed"
                onClick={() => {
                  hapticSelection();
                  setIsExplainOpen(true);
                }}
                sx={{ color: tokens.faint, ...touchTargetSx, ...focusRingSx }}
              >
                <InfoOutlined sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>

            {firstLoad && <FeedSkeleton tokens={tokens} count={2} />}

            {!firstLoad && firstLoadFailed && (
              <ErrorDisplay
                variant="card"
                error={feed.error}
                onRetry={() => void feed.refetch()}
              />
            )}

            {!firstLoad && ready && locked && (
              <EarningsLocked tokens={tokens}>
                {[0, 1].map((at) => (
                  <ResultsCardSkeleton key={at} tokens={tokens} still notes />
                ))}
              </EarningsLocked>
            )}

            {!firstLoad && ready && !locked && items.length === 0 && (
              <Typography
                data-testid="feed-empty"
                sx={{ fontSize: "0.8125rem", color: tokens.faint }}
              >
                {EMPTY_FEED_LINE}
              </Typography>
            )}

            {!firstLoad && ready && !locked && items.length > 0 && (
              <EarningsFeed
                items={items}
                tokens={tokens}
                isDark={isDark}
                reduced={reduced}
                hasNextPage={feed.hasNextPage}
                isFetchingNextPage={feed.isFetchingNextPage}
                nextPageFailed={feed.isFetchNextPageError}
                pageCount={pages?.length ?? 0}
                fetchNextPage={() => void feed.fetchNextPage()}
                onOpenReport={openReport}
                onOpenCompany={openCompany}
                anchorEntry={entry}
              />
            )}
          </Box>
        )}
      </Box>

      {firstPage !== undefined && !locked && (
        <NewItemsPill
          key={scopeId ?? "all"}
          watchlistId={scopeId}
          firstPage={firstPage}
          feedUpdatedAt={feed.dataUpdatedAt}
          cards={items}
          active={active}
          column={column}
          offsetTop={
            topBar
              ? `calc(${MOBILE_TOPBAR_HEIGHT}px + env(safe-area-inset-top, 0px) + 12px)`
              : "12px"
          }
          tokens={tokens}
          reduced={reduced}
        />
      )}

      {sheets}
    </Box>
  );
};

export default HubTab;
